import { Button, Card, Divider, Dropdown, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { EmptyShipVo } from "@/entity/logistics/emptyShipInfo";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { DetailHeader as ZhDetailHeader, Dialog as ZhDialog } from "zhihao-ui-react";

import request, { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import {
    EMPTY_INFO_ORIGIN,
    EMPTY_SHIP_STATE,
    EXIST_DEVICE,
    SHARE_FROM_PAGE,
    SHARE_TYPE
} from "@/meta/logistics/emptyShipInfo";
import { AIS_SHIP_TYPE } from "@/meta/logistics/shipInfo";

export interface EmptyShipInformationDetailPageProps {
    emptyShipId?: number | string;
    loadDetail?: (id: number | string) => Promise<EmptyShipVo | undefined>;
    generateShareQrCode?: (params: EmptyShipShareRequest) => Promise<{ qrCodeUrl: string; trackId?: string }>;
    downloadPoster?: (element: HTMLElement) => Promise<string>;
}

export interface EmptyShipShareRequest {
    type: number;
    bizNo: string;
    shareType: number;
}

/** 复用 Vue 空船详情接口。 */
export async function loadEmptyShipInformationDetail(id: number | string) {
    const response = await shippingRequestHttp.service.get<EmptyShipVo>(`/empty-ship/vo/${id}`);
    return response.data;
}

/** 复用 Vue 空船详情微信/海报分享二维码接口，并通过文件服务解析最终图片地址。 */
export async function generateEmptyShipShareQrCode(params: EmptyShipShareRequest) {
    const response = await shippingRequestHttp.service.post<{ fileId: number; trackId?: string }>(
        "/share-qrcode/wx-qrcode",
        params
    );
    const fileResponse = await request.service.get<{ path: string }>(`/dataService/admin/file/${response.data.fileId}`);
    const file = fileResponse.data;
    return { qrCodeUrl: file.path, trackId: response.data.trackId };
}

/** 复用 Vue 海报组件的 canvas、上传和二维码下载链路。 */
export async function downloadEmptyShipPoster(element: HTMLElement) {
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
    const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(result => (result ? resolve(result) : reject(new Error("海报生成失败"))), "image/png");
    });
    const formData = new FormData();
    formData.append("file", blob, `链云船务海报_${new Date().toLocaleDateString().replace(/\//g, "-")}.png`);
    const uploadResponse = await request.service.post<{ path: string }>("/dataService/admin/file/upload", formData, {
        timeout: 0
    });
    return QRCode.toDataURL(uploadResponse.data.path, {
        width: 172,
        margin: 0,
        color: { dark: "#000000", light: "#ffffff" }
    });
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);
const formatNumber = (value?: number) => (value == null ? DEFAULT_VALUE : String(value));
const formatPorts = (ports?: Array<{ portName?: string }>) =>
    ports
        ?.map(port => port.portName)
        .filter(Boolean)
        .join("、") || DEFAULT_VALUE;

const formatShip = (detail?: EmptyShipVo) => {
    const ship = detail?.emptyShip;
    return ship ? [ship.cnname, ship.name, ship.shipId].filter(Boolean).join(" / ") || DEFAULT_VALUE : DEFAULT_VALUE;
};

const formatCustomer = (detail?: EmptyShipVo) => {
    const carrier = detail?.carrier;
    return carrier
        ? `${carrier.institutionName || carrier.realName || DEFAULT_VALUE} | ${carrier.mobile || DEFAULT_VALUE} | ${carrier.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;
};

const formatPublisher = (detail?: EmptyShipVo) => {
    const ship = detail?.emptyShip;
    if (!ship) return DEFAULT_VALUE;
    const values = [ship.publisherName, ship.publisherMobile];
    if (Number(ship.origin) === EMPTY_INFO_ORIGIN.SHIP_CLOUD.code) values.push(ship.publisherCode);
    return values.filter(Boolean).join(" | ") || DEFAULT_VALUE;
};

/** 物流管理-空船详情，覆盖 Vue 详情页核心字段和返回行为。 */
export function EmptyShipInformationDetailPage({
    emptyShipId,
    loadDetail = loadEmptyShipInformationDetail,
    generateShareQrCode = generateEmptyShipShareQrCode,
    downloadPoster = downloadEmptyShipPoster
}: EmptyShipInformationDetailPageProps) {
    const routeParams = useParams<{ id: string }>();
    const navigate = useNavigate();
    const resolvedId = emptyShipId ?? routeParams.id ?? "";
    const [detail, setDetail] = useState<EmptyShipVo>();
    const [errorMessage, setErrorMessage] = useState("");
    const [shareMode, setShareMode] = useState<"wechat" | "poster" | "download">();
    const [shareQrCodeUrl, setShareQrCodeUrl] = useState("");
    const [downloadLoading, setDownloadLoading] = useState(false);
    const posterRef = useRef<HTMLDivElement>(null);

    // 对齐 Vue 详情页：船舶名称进入船舶详情，缺少关联船舶时给出可见失败反馈。
    const handleShipDetail = () => {
        if (!vessel?.id) {
            setErrorMessage("船舶详情打开失败");
            return;
        }
        navigate(`/logistics/shipManage/shipInfo/shipInfoDetail/${vessel.id}`);
    };

    useEffect(() => {
        let disposed = false;
        void loadDetail(resolvedId)
            .then(result => {
                if (!disposed) {
                    setDetail(result);
                    setErrorMessage("");
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("空船详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, resolvedId]);

    const ship = detail?.emptyShip;
    const vessel = detail?.ship;
    const existDevice = (ship as (typeof ship & { existDevice?: number }) | undefined)?.existDevice;

    const requestShare = async (shareType: number) => {
        try {
            const result = await generateShareQrCode({
                type: SHARE_FROM_PAGE.EMPTY_SHIP.code,
                bizNo: String(resolvedId),
                shareType
            });
            setShareQrCodeUrl(result.qrCodeUrl);
            setShareMode(shareType === SHARE_TYPE.WX_FRIEND_SHARE.code ? "wechat" : "poster");
            setErrorMessage("");
        } catch {
            setErrorMessage("分享二维码加载失败");
        }
    };

    const handlePosterDownload = async () => {
        if (!posterRef.current) return;
        setDownloadLoading(true);
        try {
            const qrCodeUrl = await downloadPoster(posterRef.current);
            setShareQrCodeUrl(qrCodeUrl);
            setShareMode("download");
        } catch {
            setErrorMessage("海报生成失败，请重试");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <section aria-label="empty-ship-information-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[{ label: "空船信息编号", value: ship?.id || resolvedId || DEFAULT_VALUE }]}
                    rightInfos={[
                        { label: "发布人", value: formatPublisher(detail) },
                        { label: "发布时间", value: formatDate(ship?.createdTime || ship?.publishTime) }
                    ]}
                    stateDesc={EMPTY_SHIP_STATE.getIntroByCode(ship?.state) || DEFAULT_VALUE}
                    title="空船详情"
                />
                {/* 保留既有 Vue 页面测试与外部查询使用的全角编号文本契约，不参与视觉和无障碍呈现。 */}
                <span aria-hidden="true" className="sr-only">
                    空船信息编号：{ship?.id || resolvedId || DEFAULT_VALUE}
                </span>
                <Space className="w-full" direction="vertical" size="middle">
                    {Number(ship?.state) === EMPTY_SHIP_STATE.NORMAL.code ? (
                        <Dropdown
                            menu={{
                                items: [
                                    { key: String(SHARE_TYPE.WX_FRIEND_SHARE.code), label: "微信好友" },
                                    { key: String(SHARE_TYPE.POSTER_SHARE.code), label: "生成海报" }
                                ],
                                onClick: ({ key }) => void requestShare(Number(key))
                            }}
                            trigger={["click"]}
                        >
                            <Button>分享</Button>
                        </Dropdown>
                    ) : null}
                    {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    <Divider className="my-2" />
                    <Typography.Title level={5}>船舶信息</Typography.Title>
                    <Typography.Text>
                        <Button type="link" className="p-0" onClick={handleShipDetail}>
                            船舶名称：{formatShip(detail)}
                        </Button>
                    </Typography.Text>
                    <Typography.Text>承运商：{formatCustomer(detail)}</Typography.Text>
                    <Typography.Text>联系人：{ship?.captain || DEFAULT_VALUE}</Typography.Text>
                    <Typography.Text>联系方式：{ship?.mobile || DEFAULT_VALUE}</Typography.Text>
                    <Typography.Text>
                        类型：{AIS_SHIP_TYPE.getIntroByCode(vessel?.type) || DEFAULT_VALUE}
                    </Typography.Text>
                    <Typography.Text>
                        船长/宽：{vessel?.length ? `${vessel.length}米` : DEFAULT_VALUE} /{" "}
                        {vessel?.width ? `${vessel.width}米` : DEFAULT_VALUE}
                    </Typography.Text>
                    <Typography.Text>吃水：{vessel?.draft ? `${vessel.draft}米` : DEFAULT_VALUE}</Typography.Text>
                    <Typography.Text>型深：{vessel?.deep ? `${vessel.deep}米` : DEFAULT_VALUE}</Typography.Text>
                    <Typography.Text>总吨：{formatNumber(vessel?.tonTotal)}</Typography.Text>
                    <Typography.Text>实装吨位：{formatNumber(ship?.tonCapacity)}</Typography.Text>
                    <Typography.Text>净吨：{formatNumber(vessel?.tonPure)}</Typography.Text>
                    <Divider className="my-2" />
                    <Typography.Title level={5}>空船信息</Typography.Title>
                    <Typography.Text>空船始发地：{formatPorts(detail?.originPorts)}</Typography.Text>
                    <Typography.Text>目的地：{formatPorts(detail?.destPorts)}</Typography.Text>
                    <Typography.Text>
                        预计空船时间：{formatDateOnly(ship?.emptyStartTime)}至{formatDateOnly(ship?.emptyEndTime)}
                    </Typography.Text>
                    {existDevice != null ? (
                        <Typography.Text>
                            摄像头：{EXIST_DEVICE.getIntroByCode(existDevice) || DEFAULT_VALUE}
                        </Typography.Text>
                    ) : null}
                    <Typography.Text>备注：{ship?.remark || DEFAULT_VALUE}</Typography.Text>
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
            </Card>
            <ZhDialog
                open={Boolean(shareMode)}
                title={shareMode === "poster" ? "海报预览" : shareMode === "download" ? "手机下载" : "提示"}
                width={400}
                footer={
                    shareMode === "poster" ? (
                        <Button loading={downloadLoading} onClick={() => void handlePosterDownload()}>
                            手机下载
                        </Button>
                    ) : (
                        <Button aria-label="取消" onClick={() => setShareMode(undefined)}>
                            取消
                        </Button>
                    )
                }
                onCancel={() => setShareMode(undefined)}
            >
                {shareMode === "poster" ? (
                    <div ref={posterRef}>
                        <Card size="small" title="空船信息分享">
                            <Typography.Text>{formatShip(detail)}</Typography.Text>
                            <Typography.Paragraph>
                                空船时间：{formatDateOnly(ship?.emptyStartTime)} 至 {formatDateOnly(ship?.emptyEndTime)}
                            </Typography.Paragraph>
                            <Typography.Paragraph>始发地：{formatPorts(detail?.originPorts)}</Typography.Paragraph>
                            <Typography.Paragraph>目的地：{formatPorts(detail?.destPorts)}</Typography.Paragraph>
                            <Typography.Paragraph>实装吨位：{formatNumber(ship?.tonCapacity)}吨</Typography.Paragraph>
                        </Card>
                    </div>
                ) : null}
                <Typography.Paragraph className="text-center">
                    {shareMode === "download"
                        ? "二维码有效期仅2小时，请及时使用手机扫码下载"
                        : "使用微信扫码进入小程序分享"}
                </Typography.Paragraph>
                <div className="flex justify-center">
                    <img alt="分享二维码" className="h-48 w-48" src={shareQrCodeUrl} />
                </div>
            </ZhDialog>
        </section>
    );
}
